import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import axios from '../../services/BaseService';
import { getSocket } from '../../utils/Socket';
import { toast } from 'react-toastify';
import { useGlobalAudio } from '../audio/GlobalAudioContext';
import botImg from '../../private/Images/bot-wpp.png';
import { Bot, BotOff } from 'lucide-react';
import { ChatLeftDots, Whatsapp, Headset, Clock, ShieldLock, Robot } from 'react-bootstrap-icons';
import ConfirmModal from '../confirmModal/ConfirmModal';

export function WhatsAppBell() {
  const [open, setOpen] = useState(false);
  const [chats, setChats] = useState([]);
  const [isConnected, setIsConnected] = useState(false);
  const [hasPhoneConfigured, setHasPhoneConfigured] = useState(false);
  const [botPaused, setBotPaused] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 991);

  const initialMuteState = sessionStorage.getItem('bot_muted') === 'true';
  const [isMuted, setIsMuted] = useState(initialMuteState);
  const isMutedRef = useRef(initialMuteState);

  const location = useLocation();
  const { playAlertSound, setWhatsappPendentes, setBotMuted } = useGlobalAudio();
  const prevCountRef = useRef(0);
  const statusRequestIdRef = useRef(0);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 991);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleMute = () => {
    const newState = !isMuted;
    setIsMuted(newState);
    isMutedRef.current = newState;

    sessionStorage.setItem('bot_muted', newState.toString());

    if (setBotMuted) setBotMuted(newState);

    const chatsTocando = chats.filter((chat) => !chat.silenced);
    setWhatsappPendentes(chatsTocando.length);
  };

  const fetchBotStatus = useCallback(async () => {
    // Evita que uma resposta antiga (ex: do polling de 10s disparado antes de
    // um toggle manual) chegue depois e sobrescreva um estado mais novo —
    // só aplica a resposta se nenhuma chamada mais recente foi iniciada
    // nesse meio tempo.
    const requestId = ++statusRequestIdRef.current;
    try {
      const cod_org = sessionStorage.getItem('cod_org');
      if (!cod_org) return;

      const response = await axios.get(`${process.env.REACT_APP_API_URL}/telefone/${cod_org}`);
      if (requestId !== statusRequestIdRef.current) return;

      const sessoes = response.data.data;
      const temConexao = sessoes && sessoes.some((session) => session.status === 'connected');
      const temNumero =
        sessoes && sessoes.some((session) => session.phone && String(session.phone).trim() !== '');
      setIsConnected(temConexao);
      setHasPhoneConfigured(temNumero);
      setBotPaused(!!sessoes?.[0]?.bot_pausado);
    } catch (error) {
      if (requestId !== statusRequestIdRef.current) return;
      console.error('Erro ao verificar status do WhatsApp:', error);
      setHasPhoneConfigured(false);
    }
  }, []);

  // Atualiza botPaused localmente (otimista, apos um toggle manual) e invalida
  // qualquer fetchBotStatus ja em andamento, pra ele nao sobrescrever esse
  // valor mais novo com uma leitura antiga quando a resposta chegar.
  const updateBotPaused = useCallback((newStatus) => {
    statusRequestIdRef.current++;
    setBotPaused(newStatus);
  }, []);

  const fetchActiveChats = useCallback(async () => {
    try {
      const cod_org = sessionStorage.getItem('cod_org');
      if (!cod_org) return;

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/telefone/chats/active?cod_org=${cod_org}`
      );
      const newChats = response.data;

      const chatsTocando = newChats.filter((chat) => !chat.silenced);

      if (chatsTocando.length > prevCountRef.current && !isMutedRef.current) {
        playAlertSound();
      }

      prevCountRef.current = chatsTocando.length;
      setChats(newChats);

      // Manda sempre a quantidade real (o Contexto agora decide se toca ou não baseado no setBotMuted)
      setWhatsappPendentes(chatsTocando.length);
    } catch (error) {
      console.error('Erro ao buscar chats do WhatsApp:', error);
    }
  }, [playAlertSound, setWhatsappPendentes]);

  useEffect(() => {
    fetchActiveChats();
    fetchBotStatus();

    const cod_org = sessionStorage.getItem('cod_org');
    const socket = getSocket();
    const handleUpdateChats = () => fetchActiveChats();
    const handleUpdateStatus = () => fetchBotStatus();

    socket.on(`update_whatsapp_chats_${cod_org}`, handleUpdateChats);
    socket.on('update_whatsapp_status', handleUpdateStatus);

    const interval = setInterval(() => {
      fetchActiveChats();
      fetchBotStatus();
    }, 10000);

    return () => {
      socket.off(`update_whatsapp_chats_${cod_org}`, handleUpdateChats);
      socket.off('update_whatsapp_status', handleUpdateStatus);
      clearInterval(interval);
    };
  }, [fetchActiveChats, fetchBotStatus]);

  const path = location.pathname.toLowerCase();
  const isPublicOrAdmin =
    path.includes('/menu/') ||
    path.includes('/loja/') ||
    path.includes('/auth/login') ||
    path.includes('/painel') ||
    path.includes('/admin') ||
    path.includes('/mesa/') ||
    path.includes('/autoatendimento') || // tela do cliente no tablet, sem ícone do robô
    path.includes('/chamada-senhas') || // monitor de chamada de senhas, sem ícone do robô
    path === '/' ||
    path === '/login';
  // No PDV o canto superior direito é o header (botão Sair); o robô desce para baixo dele.
  const isPdvWeb = path === '/pdv';

  if (isPublicOrAdmin) return null;
  if (!hasPhoneConfigured) return null;

  // Verde: ligado. Laranja: conectado mas bot desligado. Vermelho: desconectado
  // (mantido — na pratica o cliente costuma desligar o proprio telefone
  // quando quer desconectar, entao o icone continua aparecendo nesse caso).
  let btnColor = '#28a745';
  if (!isConnected) {
    btnColor = '#dc3545';
  } else if (botPaused) {
    btnColor = '#fd7e14';
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="btn position-fixed d-flex align-items-center justify-content-center"
        style={{
          top: isPdvWeb ? (isMobile ? 110 : 52) : isMobile ? 85 : 25,
          right: 15,
          zIndex: 1051,
          borderRadius: '50%',
          width: '50px',
          height: '50px',
          padding: 0,
          backgroundColor: btnColor,
          borderColor: btnColor,
          boxShadow: '0 4px 10px rgba(0, 0, 0, 0.3)',
          transition: 'top 0.3s ease, background-color .2s ease',
        }}
      >
        <div
          className="d-flex align-items-center justify-content-center"
          style={{ backgroundColor: '#ffffff', borderRadius: '50%', width: '38px', height: '38px' }}
        >
          <img
            src={botImg}
            alt="Bot"
            style={{ width: '26px', height: '26px', objectFit: 'contain' }}
          />
        </div>

        {!!chats.length && (
          <span
            className="position-absolute badge rounded-pill bg-danger border border-light"
            style={{ top: '-2px', right: '-4px', fontSize: '0.75rem', padding: '4px 6px' }}
          >
            {chats.length}
          </span>
        )}
      </button>

      <WhatsAppPanel
        open={open}
        onClose={() => setOpen(false)}
        chats={chats}
        onRefresh={fetchActiveChats}
        isMuted={isMuted}
        toggleMute={toggleMute}
        isConnected={isConnected}
        botPaused={botPaused}
        setBotPaused={updateBotPaused}
      />
    </>
  );
}

export function WhatsAppPanel({
  open,
  onClose,
  chats,
  onRefresh,
  isMuted,
  toggleMute,
  isConnected,
  botPaused,
  setBotPaused,
}) {
  const [loading, setLoading] = useState(null);
  const [pausing, setPausing] = useState(false);
  const [showConfirmDesligar, setShowConfirmDesligar] = useState(false);

  // --- LÓGICA DO ÍCONE DE STATUS (ligado/desligado/desconectado) ---
  let iconColor = '#28a745'; // verde: ligado
  let iconTitle = 'Bot ligado — clique para desligar';
  let statusLabel = 'Bot Ligado';
  if (!isConnected) {
    iconColor = '#dc3545'; // vermelho: desconectado
    iconTitle = 'WhatsApp desconectado — conecte o aparelho';
    statusLabel = 'Bot Desligado';
  } else if (botPaused) {
    iconColor = '#fd7e14'; // laranja: desligado, mas conectado
    iconTitle = 'Bot desligado — clique para ligar';
    statusLabel = 'Bot Desligado';
  }

  const handleTogglePause = async () => {
    if (!isConnected) return;
    setPausing(true);
    try {
      const cod_org = sessionStorage.getItem('cod_org');
      const newStatus = !botPaused;
      await axios.post(
        `${process.env.REACT_APP_API_URL}/telefone/pause`,
        { cod_org, status: newStatus }
      );
      setBotPaused(newStatus);
      toast.warn(newStatus ? '🤖 Robô DESLIGADO!' : '🤖 Robô LIGADO!');
    } catch (error) {
      toast.error('Erro ao alterar status do robô.');
    } finally {
      setPausing(false);
    }
  };

  const handleIconClick = () => {
    if (!isConnected || pausing) return;
    if (botPaused) {
      // Religar não precisa de confirmação.
      handleTogglePause();
    } else {
      // Desligar é uma ação sensível (para o envio automático de mensagens) — confirma antes.
      setShowConfirmDesligar(true);
    }
  };

  const handleResume = async (chat) => {
    setLoading(chat.id);
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/telefone/chats/resume`,
        { chatId: chat.id }
      );
      toast.success('Bot retomado com sucesso!');
      onRefresh();
    } catch (error) {
      toast.error('Erro ao retomar bot.');
    } finally {
      setLoading(null);
    }
  };

  return (
    <>
      {open && (
        <div
          className="position-fixed w-100 h-100"
          style={{ inset: 0, background: 'rgba(0,0,0,.25)', zIndex: 1050 }}
          onClick={onClose}
        />
      )}

      <div
        className="position-fixed bg-white shadow-lg"
        style={{
          top: 0,
          right: 0,
          width: 380,
          height: '100dvh',
          transform: open ? 'translateX(0)' : 'translateX(110%)',
          transition: 'transform .25s ease',
          zIndex: 1051,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          className="d-flex align-items-center justify-content-between p-3 border-bottom text-white"
          style={{ backgroundColor: iconColor, transition: 'background-color .2s ease' }}
        >
          <div
            className="d-flex align-items-center px-2 py-1"
            style={{ backgroundColor: '#fff', borderRadius: '20px' }}
          >
            <button
              type="button"
              onClick={handleIconClick}
              disabled={pausing}
              title={iconTitle}
              className="btn p-0 border-0 me-2 d-flex align-items-center justify-content-center"
              style={{
                width: '30px',
                height: '30px',
                borderRadius: '50%',
                backgroundColor: iconColor,
                boxShadow: `0 0 8px 2px ${iconColor}99`,
              }}
            >
              {pausing ? (
                <span
                  className="spinner-border spinner-border-sm text-white"
                  style={{ width: '14px', height: '14px' }}
                ></span>
              ) : isConnected && !botPaused ? (
                <Bot color="#fff" size={18} />
              ) : (
                <BotOff color="#fff" size={18} />
              )}
            </button>
            <span className="fw-semibold" style={{ color: '#000' }}>
              {statusLabel}
            </span>
          </div>
          <div className="d-flex align-items-center gap-2">
            <button
              className="btn btn-sm btn-outline-light border-0"
              onClick={toggleMute}
              title={isMuted ? 'Reativar som' : 'Silenciar'}
            >
              <i className={`bi ${isMuted ? 'bi-volume-mute-fill' : 'bi-volume-up-fill'} fs-5`}></i>
            </button>
            <button className="btn btn-sm btn-light" onClick={onClose}>
              Fechar
            </button>
          </div>
        </div>

        <div className="p-3 overflow-auto" style={{ flex: 1, backgroundColor: '#f8f9fa' }}>
          {/* --- BLOCO DE SILENCIAR O BOT --- */}
          <div className="card mb-4 border-0 shadow-sm bg-white border-start border-secondary border-4">
            <div className="card-body p-3 d-flex align-items-center justify-content-between">
              <div>
                <div className="fw-bold text-dark" style={{ fontSize: '0.9rem' }}>
                  <i className={`bi ${isMuted ? 'bi-bell-slash' : 'bi-bell'} me-1`}></i>
                  Alertas do Bot
                </div>
                <div className="text-muted" style={{ fontSize: '0.72rem' }}>
                  {isMuted ? 'Som de novos clientes desativado.' : 'Som de novos clientes ativado.'}
                </div>
              </div>

              <button
                className={`btn btn-sm ${isMuted ? 'btn-secondary' : 'btn-outline-secondary'}`}
                onClick={toggleMute}
                style={{
                  borderRadius: '20px',
                  padding: '4px 15px',
                  fontSize: '0.75rem',
                  minWidth: '95px',
                }}
              >
                <i
                  className={`bi ${isMuted ? 'bi-volume-mute-fill' : 'bi-volume-up-fill'} me-1`}
                ></i>
                {isMuted ? 'Silenciado' : 'Silenciar'}
              </button>
            </div>
          </div>

          <div
            className="text-muted fw-bold mb-3"
            style={{ fontSize: '0.7rem', letterSpacing: '1px', textTransform: 'uppercase' }}
          >
            Clientes aguardando atendimento ({chats.length})
          </div>

          {chats.length === 0 ? (
            <div className="text-center text-muted py-5 mt-4">
              <ChatLeftDots className="fs-1 opacity-25" />
              <p className="mt-2 small">Nenhum cliente na fila.</p>
            </div>
          ) : (
            chats.map((chat) => (
              <div
                key={chat.id}
                className={`card mb-3 shadow-sm border-0 border-start border-4 ${chat.silenced ? 'border-info' : 'border-warning'}`}
              >
                <div className="card-body p-3">
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <div className="fw-bold text-dark" style={{ fontSize: '1rem' }}>
                        {chat.nomeContato}
                      </div>
                      <div className="small text-muted mt-1" style={{ fontSize: '0.8rem' }}>
                        <Whatsapp className="me-1 text-success" />
                        {chat.isLid
                          ? 'Número oculto pela Meta'
                          : chat.customerPhoneFormatted || chat.customerPhone}
                      </div>
                    </div>
                    {chat.silenced ? (
                      <span
                        className="badge bg-info text-white rounded-pill px-2 py-1 shadow-sm"
                        style={{ fontSize: '0.6rem' }}
                      >
                        <Headset className="me-1" /> Atendendo
                      </span>
                    ) : (
                      <span
                        className="badge bg-warning text-dark rounded-pill px-2 py-1 shadow-sm"
                        style={{ fontSize: '0.6rem' }}
                      >
                        <Clock className="me-1" /> Esperando
                      </span>
                    )}
                  </div>

                  <div className="d-flex justify-content-end gap-2 mt-3 pt-3 border-top">
                    {chat.isLid ? (
                      <button
                        className="btn btn-sm btn-dark d-flex align-items-center px-3 shadow-sm"
                        style={{ fontSize: '0.75rem', borderRadius: '8px' }}
                        onClick={() =>
                          toast.info(
                            'Número protegido pela Meta (@lid). Por favor, abra o WhatsApp e responda a conversa mais recente.'
                          )
                        }
                      >
                        <ShieldLock className="me-2 text-warning" /> Msg
                      </button>
                    ) : (
                      <a
                        href={`https://web.whatsapp.com/send?phone=${chat.customerPhone}`}
                        target="whatsapp_atendimento"
                        rel="noopener noreferrer"
                        className="btn btn-sm btn-dark d-flex align-items-center px-3 shadow-sm"
                        style={{ fontSize: '0.75rem', borderRadius: '8px' }}
                      >
                        <Whatsapp className="me-2 text-success" /> Msg
                      </a>
                    )}

                    <button
                      className="btn btn-sm btn-success d-flex align-items-center px-3 shadow-sm"
                      onClick={() => handleResume(chat)}
                      disabled={loading === chat.id}
                      style={{ fontSize: '0.75rem', borderRadius: '8px' }}
                    >
                      {loading === chat.id ? (
                        <span className="spinner-border spinner-border-sm"></span>
                      ) : (
                        <>
                          <Robot className="me-2" /> Retomar
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      <ConfirmModal
        id="confirmDesligarBotModal"
        show={showConfirmDesligar}
        title="Desligar o Bot"
        message="Deseja desligar totalmente o atendimento e envio de mensagens automáticas do seu bot?"
        confirmText="Desligar"
        cancelText="Cancelar"
        onConfirm={() => {
          setShowConfirmDesligar(false);
          handleTogglePause();
        }}
        onClose={() => setShowConfirmDesligar(false)}
      />
    </>
  );
}
